/**
 * Summary table under the chart — the same per-bucket breakdown the charts MCP
 * App widget renders (`charts-table.util.ts`), so the numbers behind a chart are
 * readable without hovering every bar.
 */
import {
  DatasetTableConfig,
  numberColumn,
  TableConfigBuilder,
  textColumn,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { IntelligenceModels } from '@models/intelligence.model';
import TableRow = IntelligenceModels.Charts.TableRow;

/** Row height the virtual-scroll viewport lays rows out at. */
export const TABLE_ROW_HEIGHT = 44;

/** Header row: 12px padding top and bottom, one line of text, and its border. */
export const TABLE_HEADER_HEIGHT = 45;

const LABEL_COLUMN_WIDTH = 'minmax(140px, 1fr)';
const SERIES_COLUMN_WIDTH = 'minmax(90px, 0.6fr)';

/** Column key for the Nth series — the single place the `s{n}` convention is defined. */
export function seriesKey(index: number): string {
  return `s${index}`;
}

/**
 * Column headers are series labels, which are server-supplied free text rather
 * than translation keys — the table renders an unknown key verbatim, so they
 * pass through as-is while the label column stays translated.
 */
export function createChartResultTableConfig(
  series: IntelligenceModels.Charts.SeriesItem[],
): DatasetTableConfig<TableRow> {
  return new TableConfigBuilder<TableRow>()
    .columns([
      textColumn<TableRow>('label', 'CHART_RESULT.TABLE.LABEL', {
        width: LABEL_COLUMN_WIDTH,
        mobileVisible: true,
      }),
      ...series.map((item, index) =>
        numberColumn<TableRow>(seriesKey(index), item.label, {
          width: SERIES_COLUMN_WIDTH,
          alignCenter: true,
          mobileVisible: true,
        }),
      ),
    ])
    .trackBy('id')
    .rowHeight(TABLE_ROW_HEIGHT)
    .build();
}

/** One row per x-axis label, with a cell per series (missing values read as 0). */
export function buildChartResultTableRows(
  labels: string[],
  series: IntelligenceModels.Charts.SeriesItem[],
): TableRow[] {
  return labels.map((label, index) => {
    const row: TableRow = { id: String(index), label };
    series.forEach((item, seriesIndex) => {
      row[seriesKey(seriesIndex)] = item.values[index] ?? 0;
    });
    return row;
  });
}
